/**
 * The slate's state: at most six slots, persisted, with titles the diver owns.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { DiveConditions } from "../lib/conditions";
import {
  compileShelf,
  loadShelf,
  MAX_SLOTS,
  newSlot,
  saveShelf,
  type CompiledSlot,
  type ShelfSlot,
} from "../lib/shelf";

export interface ShelfApi {
  slots: CompiledSlot[];
  full: boolean;
  add: (conditions: DiveConditions, title?: string) => void;
  rename: (id: string, title: string) => void;
  remove: (id: string) => void;
  move: (id: string, direction: -1 | 1) => void;
  conditionsOf: (id: string) => DiveConditions | undefined;
}

export function useShelf(): ShelfApi {
  const [slots, setSlots] = useState<ShelfSlot[]>(() => loadShelf());

  useEffect(() => {
    saveShelf(slots);
  }, [slots]);

  const add = useCallback((conditions: DiveConditions, title?: string) => {
    setSlots((current) =>
      current.length >= MAX_SLOTS ? current : [...current, newSlot(conditions, title)],
    );
  }, []);

  const rename = useCallback((id: string, title: string) => {
    setSlots((current) =>
      current.map((s) => (s.id === id ? { ...s, title: title.slice(0, 40) } : s)),
    );
  }, []);

  const remove = useCallback((id: string) => {
    setSlots((current) => current.filter((s) => s.id !== id));
  }, []);

  const move = useCallback((id: string, direction: -1 | 1) => {
    setSlots((current) => {
      const from = current.findIndex((s) => s.id === id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }, []);

  const compiled = useMemo(() => compileShelf(slots), [slots]);

  const conditionsOf = useCallback(
    (id: string) => slots.find((s) => s.id === id)?.conditions,
    [slots],
  );

  return {
    slots: compiled,
    full: slots.length >= MAX_SLOTS,
    add,
    rename,
    remove,
    move,
    conditionsOf,
  };
}
